/* eslint-disable no-comments/disallowComments */

/**
 * ANALYTICS CONSTANTS
 *
 * Centralized location for all analytics event names and property names.
 * Use these constants instead of hardcoded strings to prevent typos and enable refactoring.
 *
 * Naming Convention (shared with platform-backend, which reports into the same
 * PostHog project — see .agents/skills/product-analytics/SKILL.md):
 * - Events: Title Case words separated by spaces, past-tense verb last, and
 *   acronyms stay uppercase (e.g., "KB Searched", "File Upload Failed")
 * - Property keys: camelCase (e.g., taskId, messageLength)
 *   Matches JavaScript conventions and useParams() output
 * - Property values: lowercase snake_case (e.g., machine_user), since those are
 *   enum members rather than keys
 */

// ============================================================================
// EVENT NAMES
// ============================================================================

export const AnalyticsEvent = {
  // Knowledge Base
  KB_SEARCHED: "KB Searched",
  KB_SEARCH_RESULT_CLICKED: "KB Search Result Clicked",
  KB_EDITOR_TOGGLED: "KB Editor Toggled",
  KB_SECTION_UPDATED: "KB Section Updated",
  KB_SECTION_VERIFIED: "KB Section Verified",
  KB_SECTION_UNVERIFIED: "KB Section Unverified",
  KB_SECTION_CREATED: "KB Section Created",
  KB_CREATED: "KB Created",
  KB_COMMENT_THREAD_CREATED: "KB Comment Thread Created",
  KB_COMMENT_THREAD_COMMENT_ADDED: "KB Comment Thread Comment Added",
  KB_COMMENT_THREAD_RESOLVED: "KB Comment Thread Resolved",
  KB_COMMENT_THREAD_DELETED: "KB Comment Thread Deleted",
  // File Upload
  FILE_UPLOAD_STARTED: "File Upload Started",
  FILE_UPLOAD_SUCCESS: "File Upload Success",
  FILE_UPLOAD_FAILED: "File Upload Failed",
  // COPILOT
  COPILOT_MESSAGE_SENT: "Copilot Message Sent",
  COPILOT_OPENED: "Copilot Opened",
  COPILOT_CLOSED: "Copilot Closed",
  // Labelling
  LABELLING_FILTER_TOGGLED: "Labelling Filter Toggled",
  LABELLING_FEEDBACK_SUBMITTED: "Labelling Feedback Submitted",
  // Navigation
  NAVBAR_LINK_CLICKED: "Navbar Link Clicked",
  NAVBAR_UPLOAD_CLICKED: "Navbar Upload Clicked",
  // Review
  REVIEW_PUBLISHED: "Review Published",
  REVIEW_REJECTED: "Review Rejected",
  REVIEW_EVIDENCE_OPENED: "Review Evidence Opened",
  REVIEW_HUNK_ACCEPTED: "Review Hunk Accepted",
  REVIEW_HUNK_REJECTED: "Review Hunk Rejected",
  REVIEW_ACCEPT_ALL: "Review Accept All",
  REVIEW_REJECT_ALL: "Review Reject All",
} as const;

// ============================================================================
// PROPERTY NAMES
// ============================================================================

export const AnalyticsProperty = {
  // IDs
  TASK_ID: "taskId",
  KB_ID: "kbId",
  VERIFIED: "verified",
  // File properties
  FILE_NAME: "fileName",
  FILE_SIZE: "fileSize",
  FILE_TYPE: "fileType",
  // Message properties
  MESSAGE_LENGTH: "messageLength",
  // Search properties
  SEARCH_COLUMN: "searchColumn",
  // View properties
  VIEW_TYPE: "viewType",
  MODE: "mode",
  // Navigation
  HREF: "href",
  LABEL: "label",
  // Error tracking
  ERROR_MESSAGE: "errorMessage",
  ERROR_CODE: "errorCode",
  SURFACE: "surface",
  API_METHOD: "apiMethod",
  API_PATHNAME: "apiPathname",
  API_STATUS: "apiStatus",
  API_STATUS_TEXT: "apiStatusText",
  // Flags and states
  IS_OPEN: "isOpen",
  // Misc
  INDEX: "index",
  APP_VERSION: "appVersion",
} as const;

// ============================================================================
// ERROR SURFACES
// ============================================================================

export const ErrorSurface = {
  AGENT_CONVERSATION_CREATE: "agent_conversation_create",
  AGENT_MESSAGE_SEND: "agent_message_send",
  APP: "app",
  ARTICLE_ANALYTICS: "article_analytics",
  COPILOT_INBOX: "copilot_inbox",
  COPILOT_MESSAGE_SEND: "copilot_message_send",
  COPILOT_TASK: "copilot_task",
  KB_PERFORMANCE_ANALYTICS: "kb_performance_analytics",
  OVERVIEW_ANALYTICS: "overview_analytics",
  REVIEW_EVIDENCE: "review_evidence",
  REVIEW_PROPOSAL_MERGE: "review_proposal_merge",
  REVIEW_PROPOSAL_REJECT: "review_proposal_reject",
  WORKFLOW_ANALYTICS: "workflow_analytics",
} as const;

// Type exports for use in other files
export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
export type AnalyticsPropertyName =
  (typeof AnalyticsProperty)[keyof typeof AnalyticsProperty];
export type ErrorSurfaceName = (typeof ErrorSurface)[keyof typeof ErrorSurface];
